import { useRef, useState, useCallback, type ChangeEvent } from 'react'
import { useSessaoCapturaCtx } from '../../contexts/SessaoCapturaContext'
import { compressImage } from '@/lib/compressImage'
import { toast } from 'sonner'

interface ToolBtnProps {
  icon: string
  label: string
  color?: string
  active?: boolean
  onClick: () => void
}

interface SessaoCapturaToolbarProps {
  onOpenText: () => void
  onOpenCanvas: () => void
  onOpenResumo: () => void
  onOpenCamera: () => void
}

const BTN_BASE: React.CSSProperties = {
  width: 52, height: 52, borderRadius: 14,
  background: 'rgba(255,255,255,0.07)',
  border: '1px solid rgba(255,255,255,0.09)',
  display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', flexShrink: 0, transition: 'background 0.15s',
}

function ToolBtn({ icon, label, color, active, onClick }: ToolBtnProps) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      title={label}
      style={{
        ...BTN_BASE,
        background: active ? `rgba(196,212,74,0.15)` : BTN_BASE.background,
        border: active ? '1px solid rgba(196,212,74,0.30)' : BTN_BASE.border,
      }}
    >
      <span className="material-symbols-outlined text-[22px]" style={{ color: active ? 'var(--color-hero)' : (color ?? '#aaa') }}>
        {icon}
      </span>
    </button>
  )
}

export default function SessaoCapturaToolbar({ onOpenText, onOpenCanvas, onOpenResumo, onOpenCamera }: SessaoCapturaToolbarProps) {
  const { addNota } = useSessaoCapturaCtx()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [recState, setRecState] = useState<'idle' | 'recording'>('idle')
  const [recSecs,  setRecSecs]  = useState<number>(0)
  const recRef      = useRef<MediaRecorder | null>(null)
  const chunksRef   = useRef<Blob[]>([])
  const timerRef    = useRef<ReturnType<typeof setInterval> | null>(null)
  const limitRef    = useRef<ReturnType<typeof setTimeout> | null>(null)
  const MAX_AUDIO_SECS = 30

  // ── Foto (fallback para navegadores sem getUserMedia) ──
  const handleFileChange = useCallback(async (e: ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? [])
    for (const raw of files) {
      const { file } = await compressImage(raw, { maxDimension: 1200, quality: 0.8, minSizeToCompress: 0 })
      await addNota({ tipo: 'FOTO', file })
    }
    e.target.value = ''
  }, [addNota])

  // Câmera: usa inline se getUserMedia disponível, senão fallback input
  const handleFoto = useCallback(() => {
    if (navigator.mediaDevices?.getUserMedia && onOpenCamera) {
      onOpenCamera()
    } else {
      fileInputRef.current?.click()
    }
  }, [onOpenCamera])

  // ── Áudio ──
  const toggleAudio = useCallback(async () => {
    if (recState === 'recording') {
      clearTimeout(limitRef.current)
      recRef.current?.stop()
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      chunksRef.current = []
      const rec = new MediaRecorder(stream)
      recRef.current = rec
      setRecSecs(0)
      timerRef.current = setInterval(() => setRecSecs(s => s + 1), 1000)

      rec.ondataavailable = e => { if (e.data.size > 0) chunksRef.current.push(e.data) }
      rec.onstop = async () => {
        clearInterval(timerRef.current)
        clearTimeout(limitRef.current)
        stream.getTracks().forEach(t => t.stop())
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' })
        await addNota({ tipo: 'AUDIO', file: blob })
        setRecState('idle')
        setRecSecs(0)
      }
      rec.start()
      setRecState('recording')
      // Auto-stop em 30s
      limitRef.current = setTimeout(() => {
        if (recRef.current?.state === 'recording') {
          recRef.current.stop()
          toast('Limite de 30s atingido — áudio salvo automaticamente')
        }
      }, MAX_AUDIO_SECS * 1000)
    } catch (err) {
      if (err?.name === 'NotAllowedError' || err?.name === 'PermissionDeniedError') {
        toast.error('Permissão de microfone negada. Habilite nas configurações do navegador.')
      }
    }
  }, [recState, addNota])

  const isRecording = recState === 'recording'

  return (
    <div
      className="flex-shrink-0 flex flex-col gap-3 px-4 py-3"
      style={{
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        background: 'rgba(12,12,12,0.85)',
        borderTop: '1px solid rgba(255,255,255,0.07)',
      }}
    >
      {/* Indicador de gravação de áudio */}
      {isRecording && (
        <div className="flex items-center gap-2 px-1">
          <span className="w-2 h-2 rounded-full animate-pulse shrink-0" style={{ background: 'var(--color-vermelho)' }} />
          <span className="text-[11px]" style={{ color: 'var(--color-vermelho)' }}>
            Gravando áudio — {recSecs}s / {MAX_AUDIO_SECS}s
          </span>
          <span className="text-[10px] text-[#555] ml-auto">Toque para parar</span>
        </div>
      )}

      {/* Botões de captura */}
      <div className="flex items-center gap-2">
        {/* Hidden file input para câmera */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          multiple
          className="hidden"
          onChange={handleFileChange}
        />

        <ToolBtn icon="photo_camera" label="Foto"    onClick={handleFoto} />
        <ToolBtn icon="mic"          label="Áudio"   color={isRecording ? 'var(--color-vermelho)' : undefined} active={isRecording} onClick={toggleAudio} />
        <ToolBtn icon="draw"         label="Croqui"  onClick={onOpenCanvas} />
        <ToolBtn icon="notes"        label="Texto"   onClick={onOpenText} />

        {/* Encerrar */}
        <button
          onClick={onOpenResumo}
          className="flex-1 h-[52px] rounded-[14px] text-[12px] font-semibold tracking-wide border-none cursor-pointer transition-colors"
          style={{ background: 'rgba(217,48,37,0.12)', color: 'var(--color-vermelho)', border: '1px solid rgba(217,48,37,0.20)' }}
        >
          Encerrar
        </button>
      </div>
    </div>
  )
}
